import classnames from 'classnames'
import React from 'react'

import { redGreenGradient } from '../../shared/gradients'
import TilesStore from '../stores/tilesStore'
import { FuturesTimeframe, LabelStyle, LayoutType, TileStyle } from '../types'
import { futuresDomains, generateColorScale, layout } from '../utils'
import { Divider } from './Divider'
import { LineLabel, LineWithEndingLabel, PlainLabel, SideLabel } from './Labels'
import { MapCondensedTile } from './Tile'
import { MapTile } from './redesign/MapTile'
import { TileWithBorder } from './redesign/TileWithBorder'

const COLS = 8
const colorScale = generateColorScale(futuresDomains, 'd', redGreenGradient)

interface NewLayoutWrapperProps {
  type: LayoutType
  tileStyle: TileStyle
  labelStyle: LabelStyle
  showSparkline: boolean
  activeTimeframe: string
}

function getTileComponent(tileStyle: TileStyle) {
  switch (tileStyle) {
    case TileStyle.Map:
      return MapTile
    case TileStyle.MapCondensed:
      return MapCondensedTile
    default:
      return TileWithBorder
  }
}

function getLabelComponent(labelStyle: LabelStyle) {
  switch (labelStyle) {
    case LabelStyle.Plain:
    case LabelStyle.PlainColor:
      return PlainLabel
    case LabelStyle.Side:
    case LabelStyle.SideColor:
      return SideLabel
    case LabelStyle.Line:
      return LineLabel
    case LabelStyle.LineWithEnding:
      return LineWithEndingLabel
    default:
      return () => null
  }
}

export function Layout(props: NewLayoutWrapperProps) {
  switch (props.type) {
    case LayoutType.NewHorizontal:
      return <LayoutHorizontal {...props} />
    case LayoutType.NewGroups:
      return <LayoutGroups {...props} />
    default:
      return null
  }
}

function LayoutHorizontal(props: NewLayoutWrapperProps) {
  const TileComponent = getTileComponent(props.tileStyle)
  const LabelComponent = getLabelComponent(props.labelStyle)

  return (
    <div className="grid grid-cols-8 gap-px bg-gray-100 dark:bg-finviz-blue-gray">
      {layout.map((row, ri) => {
        const groupName = TilesStore.getTilesGroup(row[0]).toLowerCase()
        const groupLabel = `${groupName[0].toUpperCase()}${groupName.substring(1, groupName.length)}`

        return (
          <div
            key={ri}
            className="relative gap-inherit"
            style={{ gridColumn: `span ${row.length} / span ${row.length}` }}
          >
            <LabelComponent
              groupLabel={groupLabel}
              groupName={groupName}
              rightAligned={true}
              color={[LabelStyle.PlainColor, LabelStyle.SideColor].includes(props.labelStyle)}
            />
            <div className="grid gap-inherit" style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}>
              {row.map((ticker, ci) => {
                const isSmallGroup = row.length < COLS
                const showEndCaret = isSmallGroup && ci === row.length - 1

                return (
                  <TileComponent
                    key={`${ci}-${ticker}-${props.showSparkline}`}
                    ticker={ticker}
                    colorScale={colorScale}
                    showSparkline={props.showSparkline}
                    hoverOffsetX={row.length > COLS / 2 && ci < COLS / 2 ? 0 : -320}
                    hoverOffsetY={ri < layout.length / 2 ? 105 : -275}
                  >
                    <Divider
                      className={classnames('absolute left-0 rotate-180 text-gray-50 dark:text-finviz-blue-gray', {
                        'top-3.5 xl:top-4': props.activeTimeframe === FuturesTimeframe.None,
                        'top-8 xl:top-8': props.activeTimeframe !== FuturesTimeframe.None,
                      })}
                    />
                    {showEndCaret && (
                      <Divider
                        className={classnames('absolute right-0 text-gray-50 dark:text-finviz-blue-gray', {
                          'top-3.5 xl:top-4': props.activeTimeframe === FuturesTimeframe.None,
                          'top-8 xl:top-8': props.activeTimeframe !== FuturesTimeframe.None,
                        })}
                      />
                    )}
                  </TileComponent>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function LayoutGroups(props: NewLayoutWrapperProps) {
  const TileComponent = getTileComponent(props.tileStyle)
  const LabelComponent = getLabelComponent(props.labelStyle)

  return (
    <div
      className={classnames('grid grid-cols-8 gap-x-1.5', {
        'gap-y-0.5': ![LabelStyle.Side, LabelStyle.SideColor].includes(props.labelStyle),
        'gap-y-1.5': [LabelStyle.Side, LabelStyle.SideColor].includes(props.labelStyle),
      })}
    >
      {layout.map((row, ri) => {
        const groupName = TilesStore.getTilesGroup(row[0]).toLowerCase()
        const groupLabel = `${groupName[0].toUpperCase()}${groupName.substring(1, groupName.length)}`

        return (
          <div
            key={ri}
            className={classnames('relative gap-inherit', {
              'col-span-3': ['indices', 'energy', 'softs', 'metals'].includes(groupName),
              'col-span-2': ['bonds', 'meats'].includes(groupName),
              'col-span-4': ['grains', 'currencies'].includes(groupName),
              // Side labels
              'ml-5': [LabelStyle.Side, LabelStyle.SideColor].includes(props.labelStyle),
            })}
          >
            <LabelComponent
              groupLabel={groupLabel}
              groupName={groupName}
              rightAligned={false}
              color={[LabelStyle.PlainColor, LabelStyle.SideColor].includes(props.labelStyle)}
            />
            <div
              className={classnames('grid gap-inherit', {
                'grid-cols-3': ['indices', 'energy', 'softs', 'metals'].includes(groupName),
                'grid-cols-2': ['bonds', 'meats'].includes(groupName),
                'grid-cols-4': ['grains', 'currencies'].includes(groupName),
              })}
            >
              {row.map((ticker, ci) => (
                <TileComponent
                  key={`${ci}-${ticker}-${props.showSparkline}`}
                  ticker={ticker}
                  colorScale={colorScale}
                  showSparkline={props.showSparkline}
                  hoverOffsetX={ci < COLS / 2 ? 0 : -320}
                  hoverOffsetY={ri < layout.length / 2 ? 25 : -205}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
